import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Flex from "../../components/Flex";
import RenderWithTooltip from "../../components/RenderWithTooltip";
import Text from "../../components/Text";
import { SHORT_GENDER_NAME_MAPPER } from "../../constants";
import { getAge, replaceUnderScoreAddSpace } from "../utils";
import { CHART_COLORS } from "../../components/app/provider/patient/chart";

interface PatientNameParams {
  name?: string;
  patientId: string;
  birthDate?: string | null;
  gender?: string;
  disableNavigate?: boolean;
  contactNo?: string;
  shortenGender?: boolean;
}

export const PatientName = ({
  name,
  patientId,
  birthDate,
  gender,
  disableNavigate = false,
  contactNo,
  shortenGender = true,
}: PatientNameParams) => {
  const navigate = useNavigate();

  const handleNavigate = () => {
    disableNavigate
      ? undefined
      : navigate(`/app/provider/patients/new-patient/${patientId}/index`);
  };

  return (
    <Flex
      sx={{ cursor: disableNavigate ? "default" : "pointer" }}
      onClick={handleNavigate}
    >
      <Box>
        <Text
          isTabletext
          color={disableNavigate ? undefined : CHART_COLORS.primary}
          sx={{ lineHeight: 1.2 }}
        >
          {name}
        </Text>
        {birthDate && (
          <Text
            isTabletext
            sx={{ color: CHART_COLORS.textMutedGrey, lineHeight: 1.2 }}
          >
            {`${getAge(birthDate ?? "")} yrs`}{" "}
            {gender &&
              (shortenGender
                ? `(${SHORT_GENDER_NAME_MAPPER[gender]})`
                : `(${replaceUnderScoreAddSpace(gender.toLowerCase())})`)}{" "}
            {contactNo && `| ${contactNo}`}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

interface PatientContactParams {
  contactNo?: string;
  email?: string;
}

export const PatientContact = ({ contactNo, email }: PatientContactParams) => {
  return (
    <Flex>
      <Box>
        <Text isTabletext sx={{ lineHeight: 1 }}>
          {contactNo ?? "-"}
        </Text>
        <Text
          isTabletext
          sx={{ color: CHART_COLORS.textMutedGrey, lineHeight: 1.2 }}
        >
          <RenderWithTooltip value={email} />
        </Text>
      </Box>
    </Flex>
  );
};

interface FilterAccordionProps {
  children: React.ReactNode;
  k: string;
  expanded: boolean;
  onExpand: (key: string) => void;
  label?: string;
}

export const FilterAccordion = ({
  children,
  k,
  expanded,
  onExpand,
  label,
}: FilterAccordionProps) => {
  return (
    <Accordion
      variant="outlined"
      expanded={expanded}
      onChange={() => onExpand(k)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Text>{replaceUnderScoreAddSpace(label || k)}</Text>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 1 }}>{children}</AccordionDetails>
    </Accordion>
  );
};

export const getInitialExpanded = (keys: string[]) => {
  return keys.reduce((acc, it) => ({ ...acc, [it]: false }), {});
};
